/**
 * 地図の時間フィルタ ユーティリティ
 *
 * JST の時刻レンジ（HH:mm）で位置データ・訪問を絞り込む
 */

import type { OverlandLocation, OverlandVisit } from '@/api/types/overland'

/** 地図に表示する時間帯（JST の HH:mm） */
export type MapTimeRange = {
  start: string
  end: string
}

export const FULL_DAY_RANGE: MapTimeRange = { start: '00:00', end: '23:59' }

/** 'HH:mm' を 0時起点の分に変換（不正値は null） */
export const parseTimeToMinutes = (time: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/** 1日の最終分（23:59 = 1439）。スライダーの最大値としても使う */
export const MINUTES_IN_DAY = 24 * 60 - 1

/** 0時起点の分を 'HH:mm' に変換 */
export const formatMinutes = (totalMinutes: number): string => {
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0')
  const minutes = String(totalMinutes % 60).padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * 選択日（yyyy-MM-dd）と時刻レンジから、絶対時刻（epoch ms）の範囲を求める
 * JST は UTC+9 固定のためオフセットを直接指定する
 */
const getRangeBounds = (dateStr: string, range: MapTimeRange | null): { start: number; end: number } | null => {
  if (!range) return null
  const startMinutes = parseTimeToMinutes(range.start)
  const endMinutes = parseTimeToMinutes(range.end)
  if (startMinutes === null || endMinutes === null) return null

  const [from, to] = startMinutes <= endMinutes ? [startMinutes, endMinutes] : [endMinutes, startMinutes]
  const start = new Date(`${dateStr}T${formatMinutes(from)}:00+09:00`).getTime()
  const end = new Date(`${dateStr}T${formatMinutes(to)}:59+09:00`).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return null
  return { start, end }
}

/** 時刻レンジをスライダー用の [開始分, 終了分] に変換（不正値は1日全体） */
export const rangeToMinutes = (range: MapTimeRange): [number, number] => {
  const start = parseTimeToMinutes(range.start) ?? 0
  const end = parseTimeToMinutes(range.end) ?? MINUTES_IN_DAY
  return start <= end ? [start, end] : [end, start]
}

/** スライダーの [開始分, 終了分] を時刻レンジに変換 */
export const minutesToRange = (start: number, end: number): MapTimeRange => ({
  start: formatMinutes(Math.min(start, end)),
  end: formatMinutes(Math.max(start, end)),
})

/** 時刻レンジが1日全体（絞り込みなし）かどうか */
export const isFullDayRange = (range: MapTimeRange | null): boolean => {
  if (!range) return true
  return range.start === FULL_DAY_RANGE.start && range.end === FULL_DAY_RANGE.end
}

/** 位置データを時刻レンジで絞り込む */
export const filterLocationsByTimeRange = (
  locations: OverlandLocation[],
  dateStr: string,
  range: MapTimeRange | null,
): OverlandLocation[] => {
  const bounds = getRangeBounds(dateStr, range)
  if (!bounds) return locations

  return locations.filter((location) => {
    const time = new Date(location.timestamp).getTime()
    if (Number.isNaN(time)) return false
    return time >= bounds.start && time <= bounds.end
  })
}

/** 訪問を時刻レンジで絞り込む（滞在期間がレンジと重なれば表示） */
export const filterVisitsByTimeRange = (
  visits: OverlandVisit[],
  dateStr: string,
  range: MapTimeRange | null,
): OverlandVisit[] => {
  const bounds = getRangeBounds(dateStr, range)
  if (!bounds) return visits

  return visits.filter((visit) => {
    const arrival = new Date(visit.arrival_date ?? visit.timestamp).getTime()
    if (Number.isNaN(arrival)) return false
    // 滞在中（departure なし）は現在時刻まで滞在しているとみなす
    const departureRaw = visit.departure_date ? new Date(visit.departure_date).getTime() : Date.now()
    const departure = Number.isNaN(departureRaw) ? arrival : departureRaw
    return arrival <= bounds.end && departure >= bounds.start
  })
}
